/**
 * Mock data for Kin — the Community module of Agapē
 *
 * "Kin" — your people, everywhere. Practitioners, circles, gatherings.
 * This module connects practitioners worldwide to find their tribe.
 */

export interface KulaMember {
    id: number;
    name: string;
    avatar: string;
    location: string;
    practices: string[];
    bio: string;
    latitude: number;
    longitude: number;
    isOnline: boolean;
    joinedDate: string;
    connections?: number;
    verified?: boolean;
    coverPhoto?: string;
    followers?: number;
    socialLinks?: { instagram?: string; website?: string };
    /** Real user UUID from /api/user/public — present for API-loaded members. */
    userId?: string;
}

export interface KulaCircle {
    id: number;
    name: string;
    description: string;
    /** Optional English variants — preferred when current language is not Russian. */
    nameEn?: string;
    descriptionEn?: string;
    cover: string;
    category: KulaCategory;
    memberCount: number;
    members: KulaMember[];
    upcomingEvents: KulaEvent[];
    isPrivate: boolean;
    createdBy: KulaMember;
    tags: string[];
    location?: string;
    lastActive?: string;
}

/** Pick the locale-appropriate circle name (falls back to Russian default). */
export const getCircleName = (circle: KulaCircle, language: string): string => {
    if (language !== 'ru' && circle.nameEn) return circle.nameEn;
    return circle.name;
};

/** Pick the locale-appropriate circle description (falls back to Russian default). */
export const getCircleDescription = (circle: KulaCircle, language: string): string => {
    if (language !== 'ru' && circle.descriptionEn) return circle.descriptionEn;
    return circle.description;
};

export interface KulaEvent {
    id: number;
    title: string;
    description: string;
    date: string;
    time: string;
    location: string;
    isOnline: boolean;
    circleId: number;
    hostName: string;
    hostAvatar: string;
    attendees: number;
    maxAttendees?: number;
    cover: string;
    category: KulaCategory;
    latitude: number;
    longitude: number;
}

export type KulaCategory =
    | 'meditation'
    | 'psychology'
    | 'philosophy'
    | 'denominations'
    | 'body_practices'
    | 'spiritual_practices'
    | 'morning_evening'
    | 'leadership'
    | 'motivation';

export const KULA_CATEGORIES: { key: KulaCategory; label: string; emoji: string }[] = [
    { key: 'meditation', label: 'Медитация', emoji: '🧘' },
    { key: 'psychology', label: 'Психология', emoji: '🧠' },
    { key: 'philosophy', label: 'Философия', emoji: '📖' },
    { key: 'denominations', label: 'Конфессии', emoji: '🕊️' },
    { key: 'body_practices', label: 'Телесные практики', emoji: '🩰' },
    { key: 'spiritual_practices', label: 'Духовные практики', emoji: '✨' },
    { key: 'morning_evening', label: 'Утренние и вечерние', emoji: '🌅' },
    { key: 'leadership', label: 'Лидерство', emoji: '👑' },
    { key: 'motivation', label: 'Мотивация', emoji: '🔥' },
];

// Legacy exports for backwards compat
export type SanghaCategory = KulaCategory;
export type SanghaMember = KulaMember;
export type SanghaCircle = KulaCircle;
export type SanghaEvent = KulaEvent;
export const SANGHA_CATEGORIES = KULA_CATEGORIES;

import { ImageSourcePropType } from 'react-native';

export const AVATAR_MAP: Record<string, ImageSourcePropType> = {
    'local:oksana': require('../../assets/avatars/oksana.jpg'),
    'local:maya': require('../../assets/avatars/maya.jpg'),
    'local:arjun': require('../../assets/avatars/arjun.jpg'),
    'local:luna': require('../../assets/avatars/luna.jpg'),
    'local:kai': require('../../assets/avatars/kai.jpg'),
    'local:sophia': require('../../assets/avatars/sophia.jpg'),
    'local:dmitri': require('../../assets/avatars/dmitri.jpg'),
    'local:elena': require('../../assets/avatars/elena.jpg'),
    'local:om': require('../../assets/avatars/om.jpg'),
};

export const resolveAvatar = (avatar: string): ImageSourcePropType => {
    if (AVATAR_MAP[avatar]) return AVATAR_MAP[avatar];
    return { uri: avatar };
};

export const COVER_MAP: Record<string, ImageSourcePropType> = {
    '/assets/lives/meditation.jpg': require('../../assets/lives/meditation.jpg'),
    '/assets/lives/philosophy.jpg': require('../../assets/lives/philosophy.jpg'),
    '/assets/lives/breathwork.jpg': require('../../assets/lives/breathwork.jpg'),
    '/assets/lives/yin_yoga.jpg': require('../../assets/lives/yin_yoga.jpg'),
    '/assets/lives/morning_energizer.jpg': require('../../assets/lives/morning_energizer.jpg'),
    '/assets/lives/sound_bath.jpg': require('../../assets/lives/sound_bath.jpg'),
    '/assets/lives/kundalini.jpg': require('../../assets/lives/kundalini.jpg'),
    '/assets/lives/hatha_yoga.jpg': require('../../assets/lives/hatha_yoga.jpg'),
    '/assets/lives/vinyasa_flow.jpg': require('../../assets/lives/vinyasa_flow.jpg'),
    '/assets/lives/chanting.jpg': require('../../assets/lives/chanting.jpg'),
    '/assets/lives/somatic.jpg': require('../../assets/lives/somatic.jpg'),
    '/assets/lives/gentle_stretch.jpg': require('../../assets/lives/gentle_stretch.jpg'),
    '/assets/lives/restorative_yoga.jpg': require('../../assets/lives/restorative_yoga.jpg'),
    '/assets/lives/ashtanga_yoga.jpg': require('../../assets/lives/ashtanga_yoga.jpg'),
};

export const resolveCover = (cover: string): ImageSourcePropType => {
    if (COVER_MAP[cover]) return COVER_MAP[cover];
    return { uri: cover };
};

// No mock members. The Community page shows Oksana's real profile only —
// sourced from the auth context (or a public showcase card when logged out).
export const MOCK_MEMBERS: KulaMember[] = [];

// No mock circles. The Community page shows real circles only.
export const MOCK_CIRCLES: KulaCircle[] = [];

// No mock events. Events come from the real backend per-user.
export const MOCK_EVENTS: KulaEvent[] = [];


export const KIN_STATS = {
    members: '12,400+',
    countries: 79,
    circles: '340+',
    eventsPerMonth: '500+',
};

// Legacy exports
export const KULA_STATS = KIN_STATS;
export const SANGHA_STATS = KIN_STATS;
